import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { formatPercent } from "../utils/numUtils";

const formatAmount = (amount = 0, lang = "en") => {
  const formatted = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
    Math.round((amount + Number.EPSILON) * 100) / 100
  );
  if (lang === "mr") {
    const digits = "०१२३४५६७८९";
    return "₹" + formatted.replace(/[0-9]/g, (d) => digits[parseInt(d, 10)]);
  }
  return "₹" + formatted;
};

const DonutChart = ({ data = [], hoveredIndex, onHover, lang }) => {
  const total = data.reduce((sum, item) => sum + (item.value || 0), 0);
  const radius = 85, center = 110, stroke = 35;
  let cumulative = 0;

  const getPath = (pct, start) => {
    const angle = (pct / 100) * 360;
    const startAngle = (start / 100) * 360 - 90;
    const endAngle = startAngle + angle;
    const x1 = center + radius * Math.cos((startAngle * Math.PI) / 180);
    const y1 = center + radius * Math.sin((startAngle * Math.PI) / 180);
    const x2 = center + radius * Math.cos((endAngle * Math.PI) / 180);
    const y2 = center + radius * Math.sin((endAngle * Math.PI) / 180);
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${angle > 180 ? 1 : 0} 1 ${x2} ${y2}`;
  };

  return (
    <div className="flex items-center justify-center relative" onMouseLeave={() => onHover(null)}>
      <svg width="220" height="220" className="transform -rotate-90">
        <circle cx={center} cy={center} r={radius} fill="none" stroke="#f3f4f6" strokeWidth={stroke} />
        {data.map((item, i) => {
          const pct = total === 0 ? 0 : (item.value / total) * 100;
          const path = getPath(pct, cumulative);
          const textAngle = ((cumulative + pct / 2) / 100) * 360 - 90;
          const tx = center + radius * Math.cos((textAngle * Math.PI) / 180);
          const ty = center + radius * Math.sin((textAngle * Math.PI) / 180);
          cumulative += pct;
          const isHovered = hoveredIndex === i;

          return (
            <g key={i}>
              <path
                d={path}
                fill="none"
                stroke={item.color}
                strokeWidth={stroke + (isHovered ? 4 : 0)}
                strokeLinecap="butt"
                onMouseEnter={(e) => onHover(i, e.clientX, e.clientY)}
                onMouseMove={(e) => isHovered && onHover(i, e.clientX, e.clientY)}
                onMouseLeave={() => onHover(null)}
                style={{
                  transition: "all 160ms ease",
                  transformOrigin: `${center}px ${center}px`,
                  transform: isHovered ? "scale(1.01)" : "scale(1)",
                  cursor: "pointer",
                  pointerEvents: "stroke",
                }}
              />
              <text
                x={tx} y={ty}
                fill="#fff" fontSize="11" fontWeight="700"
                textAnchor="middle" dominantBaseline="central"
                className="transform rotate-90"
                style={{ transformOrigin: `${tx}px ${ty}px`, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.25))", pointerEvents: "none" }}
              >
                {formatPercent(Math.round(pct), lang)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const DonutStatCard = ({ chartData = [], className = "" }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: null, y: null });
  const cardRef = useRef(null);
  const { t, i18n } = useTranslation();
  const lang = i18n?.language === "mr" ? "mr" : "en";

  const handleHover = (idx, clientX, clientY) => {
    setHoveredIndex(idx);
    if (clientX != null && clientY != null && cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setHoverPos({ x: clientX - rect.left, y: clientY - rect.top });
    } else {
      setHoverPos({ x: null, y: null });
    }
  };

  const getLabel = (item) => item.label || (lang === "en" ? item.labelEn : item.labelMr);
  const getAmount = (item) => parseFloat(item.received || item.amount || 0);

  const tooltipItem = hoveredIndex != null ? chartData[hoveredIndex] : null;

  // Tooltip positioning
  let tooltipLeft = 18, tooltipTop = 10;
  if (hoverPos.x != null && cardRef.current) {
    const cardW = cardRef.current.clientWidth || 400;
    tooltipLeft = Math.min(Math.max(hoverPos.x - 120, 8), cardW - 188);
  }
  if (hoverPos.y != null && cardRef.current) {
    const cardH = cardRef.current.clientHeight || 300;
    tooltipTop = Math.min(Math.max(hoverPos.y - 40, 8), cardH - 104);
  }

  return (
    <div ref={cardRef} className={`${className} relative`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-20 px-[35px]">
        {/* Donut Chart */}
        <div className="flex-shrink-0 self-center md:self-auto">
          <DonutChart data={chartData} hoveredIndex={hoveredIndex} onHover={handleHover} lang={lang} />
        </div>

        {/* Legend Table */}
        <div className="flex-1">
          <div className="overflow-x-auto">
            <div className="min-w-[400px]">
              <div className="flex items-center gap-4 mb-3 pb-2 border-b border-gray-200">
                <span className="text-sm font-normal text-[#0A0A0A] flex-1">{t("incomeExpenditure.source")}</span>
                <span className="text-sm font-normal text-[#0A0A0A] flex-1 text-right">{t("incomeExpenditure.rupee")}</span>
                <span className="text-sm font-normal text-[#0A0A0A] flex-1 text-right">{t("incomeExpenditure.percent")}</span>
              </div>
              <div className="flex flex-col gap-2">
                {chartData.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-[#0A0A0A] font-normal truncate">{getLabel(item)}</span>
                    </div>
                    <div className="text-sm text-[#0A0A0A] font-normal flex-1 text-right">
                      {formatAmount(getAmount(item), lang)}
                    </div>
                    <div className="text-sm text-[#0A0A0A] font-normal flex-1 text-right">
                      {formatPercent(item.value, lang, { decimals: 2 })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {tooltipItem && (
        <div
          role="tooltip"
          className="absolute pointer-events-none"
          style={{ left: tooltipLeft, top: tooltipTop, width: 180, zIndex: 60, transition: "transform 120ms ease, opacity 120ms ease" }}
        >
          <div style={{ background: "#212121", color: "#fff", padding: "10px 12px", borderRadius: 8, boxShadow: "0 6px 12px rgba(0,0,0,0.18)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{getLabel(tooltipItem)}</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{formatAmount(getAmount(tooltipItem), lang)}</div>
            <div style={{ fontSize: 12, opacity: 0.9 }}>{formatPercent(tooltipItem.value, lang)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonutStatCard;
